import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import GuestNavList from '../../components/GuestNavList';
import env from '../../config/env';
import { useAxios } from '../../utils/hook/useAxios';

const MAPBOX_TOKEN = env.mapboxToken;

const isCoords = (str) => /^-?\d+\.?\d*,\s*-?\d+\.?\d*$/.test((str ?? '').trim());

const reverseGeocode = async (coordStr) => {
  try {
    const [lat, lng] = coordStr.split(',').map(Number);
    const res = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${MAPBOX_TOKEN}&language=fr&types=place,locality,neighborhood,address&limit=1`
    );
    const json = await res.json();
    const feature = json.features?.[0];
    if (!feature) return coordStr;
    // Préfère le nom court (place) + contexte ville
    const city = feature.context?.find((c) => c.id.startsWith('place.'))?.text;
    return city ? `${feature.text}, ${city}` : (feature.place_name ?? coordStr);
  } catch {
    return coordStr;
  }
};

function FavoritesPage() {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locationNames, setLocationNames] = useState({}); // { vehiculeAnnonceId: resolvedName }
  const axios = useAxios();
  const user = JSON.parse(window.localStorage.getItem('me') || 'null');

  const fetchFavorites = async () => {
    if (!user?.id) return;
    try {
      const { data } = await axios.get(`/favorites/user/${user.id}`);
      const list = data || [];
      setFavorites(list);

      // Reverse geocode en parallèle les adresses qui sont des coordonnées
      const toResolve = list.filter(
        (f) => f.Annonce?.locationAddress && isCoords(f.Annonce.locationAddress)
      );
      if (toResolve.length > 0) {
        const resolved = await Promise.all(
          toResolve.map(async (f) => {
            const name = await reverseGeocode(f.Annonce.locationAddress);
            return [f.vehiculeAnnonceId, name];
          })
        );
        setLocationNames(Object.fromEntries(resolved));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFavorites();
  }, []);

  const handleRemove = async (vehiculeAnnonceId) => {
    try {
      await axios.post('/favorites/toggle', { userId: user.id, vehiculeAnnonceId });
      setFavorites((prev) => prev.filter((f) => f.vehiculeAnnonceId !== vehiculeAnnonceId));
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="container py-4">
      <div className="row">
        {/* Sidebar */}
        <div className="col-md-3 d-none d-md-block">
          <GuestNavList />
        </div>

        {/* Content */}
        <div className="col-md-9">
          <div className="d-flex align-items-center gap-2 mb-4">
            <i className="bi bi-heart-fill text-danger" style={{ fontSize: 22 }}></i>
            <h2 className="mb-0 fw-bold" style={{ fontSize: 22 }}>
              Mes favoris
            </h2>
            {!loading && (
              <span className="badge bg-secondary-subtle text-secondary ms-1 rounded-pill">
                {favorites.length}
              </span>
            )}
          </div>

          {loading && (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" />
            </div>
          )}

          {!loading && favorites.length === 0 && (
            <div className="text-center py-5">
              <i className="bi bi-heart text-muted" style={{ fontSize: 48 }}></i>
              <p className="mt-3 text-muted">Vous n'avez pas encore de favoris.</p>
              <Link to="/location-voitures" className="btn btn-primary mt-2">
                Découvrir des annonces
              </Link>
            </div>
          )}

          <div className="row g-3">
            {favorites.map((fav) => {
              const annonce = fav.Annonce;
              if (!annonce) return null;
              const vehicle = annonce.Vehicle;
              const model = vehicle?.VehicleModel;
              const host = vehicle?.UserProfile;
              const img = vehicle?.principalPhotos || vehicle?.images?.split(',')[0];
              const displayAddress =
                locationNames[fav.vehiculeAnnonceId] ?? annonce.locationAddress;

              return (
                <div key={fav.id} className="col-sm-6 col-lg-4">
                  <div className="card h-100 border-0 shadow-sm rounded-3 overflow-hidden">
                    {/* Image */}
                    <div className="position-relative">
                      <Link to={`/location-voiture/${annonce.vehiculeAnnonceId}`}>
                        <img
                          src={img || '/noimage.png'}
                          alt={model?.modele || 'Véhicule'}
                          style={{ width: '100%', height: 160, objectFit: 'cover' }}
                        />
                      </Link>
                      {/* Remove */}
                      <button
                        className="position-absolute top-0 end-0 m-2 btn btn-sm rounded-circle border-0 d-flex align-items-center justify-content-center"
                        style={{ width: 32, height: 32, background: 'rgba(255,255,255,0.9)' }}
                        onClick={() => handleRemove(annonce.vehiculeAnnonceId)}
                        title="Retirer des favoris"
                      >
                        <i className="bi bi-heart-fill text-danger" style={{ fontSize: 15 }}></i>
                      </button>
                    </div>

                    {/* Body */}
                    <div className="card-body p-3">
                      <p className="fw-bold mb-1" style={{ fontSize: 14 }}>
                        {model?.marque} {model?.modele}
                      </p>
                      {displayAddress && (
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>
                          <i className="bi bi-geo-alt me-1"></i>
                          {displayAddress}
                        </p>
                      )}
                      {host && (
                        <p className="text-muted mb-2" style={{ fontSize: 12 }}>
                          <i className="bi bi-person me-1"></i>
                          {host.firstName} {host.lastName}
                          {host.city && ` · ${host.city}`}
                        </p>
                      )}
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fw-bold text-primary" style={{ fontSize: 15 }}>
                          {annonce.reservationPrice} €
                          <span className="text-muted fw-normal" style={{ fontSize: 11 }}>
                            /jour
                          </span>
                        </span>
                        <Link
                          to={`/location-voiture/${annonce.vehiculeAnnonceId}`}
                          className="btn btn-sm btn-primary"
                          style={{ fontSize: 12 }}
                        >
                          Voir
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FavoritesPage;
